import React, { useEffect, useState } from 'react';
import theme from 'theme';
import { Colors, LoadingSpinner, Text } from 'library';
import { Grid, styled, useMediaQuery } from '@mui/material';
import { useClient } from 'shared';
import { LocationsDropDown } from './components/LocationsDropDown';
import { TimesheetsTable } from './components/TimesheetsTable';

import type { PagingResult, IBusinessLocation, ISentHomeAppointment } from 'models';
import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/new/types';
import { AddRetroHoursModal } from './components/AddRetroHoursModal';
import SentHomeCancels from './components/SentHomeCancels';
import { CenterMissingPaymentReports } from './components/CenterMissingPaymentReports';

export default function Timesheets() {
    const paramLocation = new URLSearchParams(window.location.search).get('location');
    const paramPage = new URLSearchParams(window.location.search).get('page');
    const paramUnassociatedPage = new URLSearchParams(window.location.search).get('unassociatedPage');
    const page = paramPage ? Number(paramPage) : 1;
    const unassociatedPage = paramUnassociatedPage ? Number(paramUnassociatedPage) : 1;

    const [firstLocation, setFirstLocation] = useState<IBusinessLocation | undefined>();
    const small = useMediaQuery(theme.breakpoints.down('md'));

    const {
        loading: locationsLoading,
        data: locationsData,
        error: locationsError,
    } = useClient<PagingResult<IBusinessLocation>>({
        url: 'api/business-location/?page_size=100',
    });

    useEffect(() => {
        if (locationsData?.results?.length) {
            setFirstLocation(locationsData.results[0]);
        }
    }, [locationsData]);

    const {
        send: fetchTimesheets,
        data: timesheets,
        loading: timesheetsLoading,
        error: timesheetsError,
    } = useClient<PagingResult<TimesheetWorker>>({
        url: `api/hour-report/timesheets/?location=${paramLocation || firstLocation?.id}&page=${page}`,
        options: { sendImmediately: false },
    });

    const {
        send: fetchShiftsNeedingClockedIn,
        data: shiftsNeedingClockedIn,
        loading: shiftsNeedingClockedInLoading,
        error: shiftsNeedingClockedInError,
    } = useClient<PagingResult<UnclockedInTimesheetWorker>>({
        url: `api/hour-report/shifts-to-clockin/?location=${paramLocation || firstLocation?.id}&page=${unassociatedPage}`,
        options: { sendImmediately: false },
    });

    const { send: fetchSentHomeCancellations, data: sentHomeCancellations } = useClient<ISentHomeAppointment[]>({
        url: `api/sub-shifts/sent-home-appts/?business_location_id=${paramLocation || firstLocation?.id}`,
        options: { sendImmediately: false },
    });

    useEffect(() => {
        const locationId = paramLocation || firstLocation?.id;
        if (!locationId) return;

        fetchTimesheets();
        fetchShiftsNeedingClockedIn();
        fetchSentHomeCancellations();
    }, [firstLocation?.id, paramLocation, page, unassociatedPage]);

    function setPage(newPage: number) {
        const params = new URLSearchParams(window.location.search);
        params.set('page', newPage.toString());
        const url = `timesheets?${params.toString()}`;
        window.location.href = url;
    }

    function setUnassociatedPage(newPage: number) {
        const params = new URLSearchParams(window.location.search);
        params.set('unassociatedPage', newPage.toString());
        const url = `timesheets?${params.toString()}`;
        window.location.href = url;
    }

    return (
        <Page>
            <Text variant="display">Timesheets</Text>
            {!(locationsError || timesheetsError || shiftsNeedingClockedInError) &&
                (locationsLoading ? (
                    <LoadingSpinner loadingStyle={{ color: Colors.darkNavy }} />
                ) : (
                    <>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{
                                backgroundColor: Colors.lightTurq,
                                paddingTop: '2%',
                                paddingBottom: '2%',
                                paddingLeft: '1%',
                                paddingRight: '1%',
                                marginTop: 20,
                            }}
                        >
                            <LocationsDropDown
                                data={locationsData}
                                small={small}
                                location={firstLocation?.id.toString()}
                            />
                            <AddRetroHoursModal
                                businessLocations={locationsData}
                                small={small}
                                onComplete={fetchTimesheets}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <div style={{ borderBottom: `1px solid ${Colors.mediumGrey}`, marginBottom: 20 }} />
                        </Grid>
                        {(paramLocation || firstLocation?.id) && (
                            <TimesheetsContainer>
                                {sentHomeCancellations?.length ? (
                                    <>
                                        <SpacingContainer />
                                        <SentHomeCancels
                                            overallReasons={sentHomeCancellations}
                                            refreshReasons={fetchSentHomeCancellations}
                                        />
                                        <Spacer />
                                    </>
                                ) : null}
                                {!!shiftsNeedingClockedIn?.results?.length && (
                                    <>
                                        <Text variant="h2" bold>
                                            Workers Not Clocked In ({shiftsNeedingClockedIn?.results?.length})
                                        </Text>
                                        <Text variant="body2" textStyle={{ paddingBottom: 10, marginTop: 5 }}>
                                            If these worker(s) are at the center, please clock them in.
                                        </Text>
                                        <TimesheetsTable
                                            data={shiftsNeedingClockedIn}
                                            small={small}
                                            loading={shiftsNeedingClockedInLoading}
                                            refresh={() => {
                                                fetchShiftsNeedingClockedIn();
                                                fetchTimesheets();
                                            }}
                                            page={unassociatedPage}
                                            setPage={setUnassociatedPage}
                                        />
                                        <Spacer />
                                    </>
                                )}
                                <CenterMissingPaymentReports
                                    locationId={paramLocation || firstLocation?.id}
                                    small={small}
                                    timesheetRefresh={fetchTimesheets}
                                />
                                <SpacingContainer>
                                    <Text variant="h2" bold>
                                        Recent Timesheets ({timesheets?.results?.length || 0})
                                    </Text>
                                    {timesheets?.results && timesheets.results.length > 0 && (
                                        <Text variant="body2" textStyle={{ paddingBottom: 10, marginTop: 5 }}>
                                            These are the timesheets for the past week of shifts. Please review anything
                                            that says &quot;Needs Review&quot;.
                                        </Text>
                                    )}
                                    <TimesheetsTable
                                        data={timesheets}
                                        small={small}
                                        loading={timesheetsLoading}
                                        refresh={fetchTimesheets}
                                        page={page}
                                        setPage={setPage}
                                    />
                                </SpacingContainer>
                            </TimesheetsContainer>
                        )}
                    </>
                ))}
            {(locationsError || timesheetsError) && (
                <Text variant="body1" color={Colors.error} textStyle={{ paddingTop: 20 }}>
                    Error loading data
                </Text>
            )}
        </Page>
    );
}

const Page = styled('div')({
    padding: '20px',
    paddingBottom: 100,
    paddingTop: 30,
});

const TimesheetsContainer = styled('div')({
    paddingLeft: 0,
});

const SpacingContainer = styled('div')({
    paddingBottom: 20,
});

const Spacer = styled('div')({
    width: '100%',
    borderBottom: `1px solid ${Colors.mediumGrey}`,
    marginBottom: 30,
    marginTop: 10,
});
